"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const complexData_1 = __importDefault(require("@powerednow/complexData/complexData"));
const FormDocumentManager_1 = __importDefault(require("@powerednow/shared/modules/form/FormDocumentManager"));
const entity_1 = __importDefault(require("./entity"));
const modelProperties_1 = __importDefault(require("./modelProperties"));
const modelLookup_1 = require("../modelLookup");
const constants = require('@powerednow/shared/constants').default;
const _ = require('lodash');
const ONE_HOUR_MS = 60 * 60 * 1000;
// eslint-disable-next-line no-redeclare
class FormDocument extends complexData_1.default {
    static get allowedAssociations() {
        return {
            company: {
                instance: (0, modelLookup_1.complexFindModel)('Company'),
                key: 'company',
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().company_id,
                },
            },
            job: {
                key: 'job',
                instance: (0, modelLookup_1.complexFindModel)('Job'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().job_id,
                },
            },
            site: {
                key: 'contactToAddress',
                instance: (0, modelLookup_1.complexFindModel)('ContactToAddress'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().site_id,
                },
            },
            customer: {
                key: 'customer',
                instance: (0, modelLookup_1.complexFindModel)('Customer'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().customer_id,
                },
            },
            user: {
                key: 'user',
                instance: (0, modelLookup_1.complexFindModel)('User'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().user_id,
                },
            },
            form: {
                key: 'form',
                instance: (0, modelLookup_1.complexFindModel)('Form'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().form_id,
                },
            },
            message: {
                key: 'message',
                instance: (0, modelLookup_1.complexFindModel)('Message'),
                cascadeDelete: true,
                condition: {
                    document_id: this.Entity.getFieldSymbols().id,
                },
            },
            documentLogo: {
                key: 'documentLogo',
                instance: (0, modelLookup_1.complexFindModel)('DocumentLogo'),
                single: true,
                cascadeDelete: false,
                condition: {
                    document_type: constants.LOGO.DOCUMENT_TYPE.FORM_DOCUMENT,
                },
            },
            signature: {
                key: 'signature',
                instance: (0, modelLookup_1.complexFindModel)('Signature'),
                cascadeDelete: false,
                single: true,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().signature_id,
                },
            },
            mediaGroup: {
                key: 'mediaGroup',
                instance: (0, modelLookup_1.complexFindModel)('MediaGroup'),
                cascadeDelete: false,
                single: true,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().mediagroup_id,
                },
            },
            customerMessage: {
                key: 'customerMessage',
                instance: (0, modelLookup_1.complexFindModel)('CustomerMessage'),
                cascadeDelete: true,
                condition: {
                    linked_id: this.Entity.getFieldSymbols().id,
                    linked_type: constants.MESSAGES.CUSTOMER_MESSAGE_LINK_TYPES.FORMDOCUMENT,
                },
            },
        };
    }
    static get indexedSearchConfig() {
        return {
            job: true,
        };
    }
    async getFormDocumentManager(globalObjects) {
        const form = await this.getForm();
        return new FormDocumentManager_1.default(await form.getRelated(), await this.getTemplateData(globalObjects));
    }
    async getDefaultSite() {
        const job = await this.getJob();
        return job.findSite();
    }
    async inheritFrom(otherFormDocument, globalObjects) {
        const formDocumentManager = await this.getFormDocumentManager(globalObjects);
        const otherFormDocumentManager = await otherFormDocument.getFormDocumentManager(globalObjects);
        formDocumentManager.copyFrom(otherFormDocumentManager);
        this.data.form_data = formDocumentManager.getData();
    }
    async getRelated(globalObjects) {
        const data = { ...this.data.getDataValues() };
        const formDocumentData = await this.getTemplateData(globalObjects);
        return { ...formDocumentData, ...data };
    }
    async generateGlobalObjects() {
        const company = await this.getCompany();
        const user = await this.getUser();
        return {
            currentCompany: company,
            currentUser: user,
        };
    }
    async getPermanentTemplateData() {
        return {};
    }
    async getMessageTemplateData(messageType) {
        return this.getTemplateData();
    }
    async getTemplateData(globalObjects = {}) {
        if (this.data.status !== constants.FORMS.STATUSES.DRAFT) {
            return this.data.form_data;
        }
        return this.getDefaultTemplateData(globalObjects);
    }
    async getDefaultTemplateData(globalObjects) {
        return {
            form: this.data.form_data.form || {},
            formDocument: await this.getFormDocumentData(),
            company: await this.getCompanyData(globalObjects),
            customer: await this.getCustomerData(),
            user: await this.getUserData(globalObjects),
            companyForm: await this.getCompanyFormData(),
        };
    }
    async getCompanyFormData() {
        const form = await this.getForm();
        const companyForm = await form.getCompanyForm();
        const companyFormData = await companyForm.getData();
        return {
            companyId: companyFormData.company_id,
            emailConfig: companyFormData.email_config,
            numberingConfig: companyFormData.numbering_config,
            reminderConfig: companyFormData.reminder_config,
        };
    }
    async updateTemplateData(globalObjects) {
        if (this.data.form_data.user) {
            const userData = await this.getUserData(globalObjects);
            this.data.form_data.user = _.merge({}, this.data.form_data.user, userData);
        }
    }
    /**
     * Update the editing info on form_data. We must not update if form_data is empty, as various checks in
     * FormDocumentManager uses that to tell if a form needs default values.
     *
     * Return value must indicate if form_date changed in this function
     * @param userId
     * @param timestamp
     */
    updateEditInfo(userId, timestamp) {
        if (_.isEmpty(this.data?.form_data?.form)) {
            return false;
        }
        const { editInfo } = this.data?.form_data?.form || {};
        const { timestamp: currentEditingStartTime } = editInfo || {};
        const now = Date.now();
        if (!editInfo || (now - currentEditingStartTime) > ONE_HOUR_MS) {
            this.data.form_data = { ...this.data.form_data, form: { ...(this.data.form_data.form || {}), editInfo: { userId, timestamp } } };
            return true;
        }
        return false;
    }
    /**
     * Remove the editing info on form_data. Only allow removel if the current user is in editInfo or it is in
     * editing state for an hour.
     *
     * Return value must indicate if form_date changed in this function
     * @param userId
     */
    removeOwnEditInfo(userId) {
        const { editInfo, ...restForm } = this.data.form_data.form;
        const { timestamp: currentEditingStartTime } = editInfo || {};
        const now = Date.now();
        if (editInfo?.userId === userId || (now - currentEditingStartTime) > ONE_HOUR_MS) {
            this.data.form_data = { ...this.data.form_data, form: restForm };
            return true;
        }
        return false;
    }
    async getFormDocumentData() {
        const form = await this.getForm();
        const formType = await form.getFormType();
        return {
            displayNumber: this.data.display_number,
            form: {
                id: form.data.id,
                description: form.data.description,
                title: form.data.title,
                name: formType.data.name,
                version: form.data.version,
            },
        };
    }
    async getCompanyData(globalObjects) {
        const documentLogo = await this.getDocumentLogo();
        const company = globalObjects.currentCompany;
        const companyAddress = await company.getCompanyAddress();
        const companyData = _.cloneDeep(company.data.getPureDataValues());
        const companyAddressData = _.cloneDeep(companyAddress.data.getPureDataValues());
        const documentLogoData = documentLogo ? _.cloneDeep(documentLogo.data.getPureDataValues()) : null;
        return {
            email: companyData.email,
            form: companyData.form_data || {},
            name: companyData.name,
            phone: companyData.phone,
            logoUrl: (documentLogoData && documentLogoData.url) ? documentLogoData.url : null,
            address: {
                address1: companyAddressData.address1,
                address2: companyAddressData.address2,
                city: companyAddressData.city,
                state: companyAddressData.state,
                postcode: companyAddressData.postcode,
            },
        };
    }
    async getUserData(globalObjects) {
        const user = globalObjects.currentUser;
        const userRelatedData = await user.getRelated();
        const formData = _.isEmpty(userRelatedData.form_data) || !userRelatedData.form_data ? {
            competentPersonsScheme: 'None',
        } : userRelatedData.form_data;
        return {
            form: formData,
            fullName: userRelatedData.fullName,
            signatureUrl: userRelatedData.signatureUrl,
            role: userRelatedData.role,
        };
    }
    async getCustomerData() {
        const customer = await this.getCustomer();
        const customerRelatedData = await customer.getTemplateData();
        const job = await this.getJob();
        const site = await this.findSite();
        const address = await site.getAddress();
        const contact = await site.getContact();
        const addressTemplateData = await address.getTemplateData(this.data.plot);
        const contactTemplateData = await contact.getTemplateData();
        const phone = (await contact.getPhone())?.data.value;
        return {
            jobId: job.data.id,
            id: customerRelatedData.id,
            fullName: customerRelatedData.fullName,
            companyName: customer.data.company,
            address: {
                address1: customerRelatedData.address[0].address1,
                address2: customerRelatedData.address[0].address2,
                city: customerRelatedData.address[0].city,
                state: customerRelatedData.address[0].state || customerRelatedData.address[0].county,
                postcode: customerRelatedData.address[0].postcode,
            },
            site: {
                id: contactTemplateData.id,
                fullName: contactTemplateData.fullName,
                address: {
                    plot: addressTemplateData.addressPlot,
                    address1: addressTemplateData.address1,
                    address2: addressTemplateData.address2,
                    city: addressTemplateData.city,
                    state: addressTemplateData.state || addressTemplateData.county,
                    postcode: addressTemplateData.postcode,
                },
                phone,
            },
        };
    }
    getStatus() {
        return this.data.status;
    }
    async findSite() {
        return (await this.getSite()) || (await this.getJob()).findSite();
    }
    async findJobSite() {
        return (await this.getJob()).findSite();
    }
    composeDisplayNumber(documentNumber, numberingConfig) {
        const { prefix } = numberingConfig;
        const { suffix } = numberingConfig;
        const { incrementalLength } = numberingConfig;
        let number = new Array(Number(incrementalLength)).join('0') + documentNumber;
        number = number.slice(-1 * Math.max(incrementalLength, (String(documentNumber)).length));
        return prefix + number + suffix;
    }
}
FormDocument.Entity = entity_1.default;
FormDocument.modelProperties = modelProperties_1.default;
FormDocument.MessageTypeSpecificDataCollectors = {};
FormDocument.MessageTypeSpecificPermanentDataCollectors = {};
exports.default = FormDocument;
//# sourceMappingURL=../../../../src/models/formDocument/index.js.map